import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ArrowLeft, User, Lock, Save, Building2, Shield, Crown, Users } from 'lucide-react';
import { toast } from 'sonner';
import Header from './Header';

const API = '/api';

const Profile = () => {
  const navigate = useNavigate();
  const { user, currentWorkspace, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [owners, setOwners] = useState([]);
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    company_name: user?.company_name || '',
    new_password: '',
    confirm_password: ''
  });

  // Keep form in sync when the user profile hydrates (e.g. /auth/me returns after mount).
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      full_name: prev.full_name || user?.full_name || '',
      company_name: prev.company_name || user?.company_name || '',
    }));
  }, [user?.full_name, user?.company_name]);

  // Fetch workspace owners
  useEffect(() => {
    if (currentWorkspace && currentWorkspace.id !== 'super_admin') {
      fetchWorkspaceOwners();
    }
  }, [currentWorkspace]);

  const fetchWorkspaceOwners = async () => {
    setLoadingOwners(true);
    try {
      const response = await axios.get(`${API}/workspace/owners`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setOwners(response.data.owners || []);
    } catch (error) {
      console.error('Failed to fetch workspace owners:', error);
    } finally {
      setLoadingOwners(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    const companyName = (formData.company_name || '').trim();
    if (!companyName) {
      toast.error('Company name is required');
      return;
    }
    setLoading(true);

    try {
      await axios.put(
        `${API}/profile`,
        { full_name: formData.full_name, company_name: companyName },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      toast.success('Profile updated successfully');

      // Update AuthContext user state
      updateUser({ full_name: formData.full_name, company_name: companyName });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (formData.new_password !== formData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (formData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.put(
        `${API}/profile/password`,
        { new_password: formData.new_password },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      toast.success('Password changed successfully');
      
      // Clear password fields
      setFormData({
        ...formData,
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const getDashboardUrl = () => {
    if (!user) return '/';
    if (user.role === 'super_admin') return '/admin';
    if (user.role === 'auditor') return '/auditor/dashboard';
    if (user.role === 'employee') return '/employee/portal';
    return '/dashboard';
  };

  return (
    <div className="min-h-screen bg-appbg">
      <Header />
      
      <div className="max-w-4xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2">My Profile</h1>
          <p className="text-slate-600">Manage your account settings and preferences</p>
        </div>

        <div className="space-y-6">
          {/* Account Overview Card */}
          <Card className="border-0 shadow-lg bg-gradient-to-br from-navy-50 to-white">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-navy-600" />
                <span>Account Overview</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-slate-600 text-sm">Email</Label>
                  <p className="text-lg font-medium text-slate-900">{user?.email}</p>
                </div>

                <div>
                  <Label className="text-slate-600 text-sm">Company</Label>
                  <div className="flex items-center space-x-2">
                    <Building2 className="w-4 h-4 text-navy-600" />
                    {user?.company_name ? (
                      <p className="text-lg font-medium text-slate-900" data-testid="profile-company-name-display">
                        {user.company_name}
                      </p>
                    ) : (
                      <p className="text-sm text-amber-700 italic" data-testid="profile-company-name-missing">
                        Not set — please add below
                      </p>
                    )}
                  </div>
                </div>
                
                {currentWorkspace && currentWorkspace.id !== 'super_admin' && (
                  <>
                    <div>
                      <Label className="text-slate-600 text-sm">Workspace</Label>
                      <div className="flex items-center space-x-2">
                        <Building2 className="w-4 h-4 text-navy-600" />
                        <p className="text-lg font-medium text-slate-900">{currentWorkspace.name}</p>
                      </div>
                    </div>
                    
                    <div>
                      <Label className="text-slate-600 text-sm">Your Role</Label>
                      <div className="mt-1">
                        <Badge className="capitalize bg-emerald-100 text-navy-700 border-emerald-200">
                          {currentWorkspace.role?.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Workspace Owners Section */}
                    <div className="md:col-span-2">
                      <Label className="text-slate-600 text-sm flex items-center gap-1.5">
                        <Crown className="w-3.5 h-3.5 text-amber-500" />
                        Workspace Owner{owners.length > 1 ? 's' : ''}
                      </Label>
                      <div className="mt-2">
                        {loadingOwners ? (
                          <p className="text-sm text-slate-500">Loading...</p>
                        ) : owners.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {owners.map((owner) => (
                              <div 
                                key={owner.id}
                                className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                              >
                                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                                  <Crown className="w-4 h-4 text-amber-600" />
                                </div>
                                <div>
                                  <p className="text-sm font-medium text-slate-900">
                                    {owner.full_name || 'No name set'}
                                  </p>
                                  <p className="text-xs text-slate-500">{owner.email}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500">No owners found</p>
                        )}
                      </div>
                    </div>
                  </>
                )}
                
                {currentWorkspace?.role === 'super_admin' && (
                  <div>
                    <Label className="text-slate-600 text-sm">Role</Label>
                    <div className="mt-1">
                      <Badge className="capitalize bg-purple-100 text-purple-700 border-purple-200">
                        Super Admin
                      </Badge>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        
          {/* Profile Information */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="w-5 h-5 text-navy-600" />
                <span>Profile Information</span>
              </CardTitle>
              <CardDescription>
                Update your personal information
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateProfile} className="space-y-4" noValidate>
                <div>
                  <Label htmlFor="full_name">Full Name</Label>
                  <Input
                    id="full_name"
                    type="text"
                    value={formData.full_name}
                    onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                    placeholder="Enter your full name"
                    required
                    data-testid="profile-full-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="company_name">
                    Company Name <span className="text-red-500" aria-hidden>*</span>
                  </Label>
                  <Input
                    id="company_name"
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="e.g. Acme, Inc."
                    required
                    minLength={1}
                    maxLength={120}
                    data-testid="profile-company-name-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Used on audit reports, evidence exports, and your account summary.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-navy-600 hover:bg-navy-700"
                  data-testid="profile-save-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Lock className="w-5 h-5 text-navy-600" />
                <span>Change Password</span>
              </CardTitle>
              <CardDescription>
                Update your password to keep your account secure
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <Label htmlFor="new_password">New Password</Label>
                  <Input
                    id="new_password"
                    type="password"
                    value={formData.new_password}
                    onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                    placeholder="Enter new password (min 8 characters)"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="confirm_password">Confirm New Password</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                    placeholder="Confirm new password"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-navy-600 hover:bg-navy-700"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {loading ? 'Changing...' : 'Change Password'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Profile;
